"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  apiFetch,
  clearSession,
  saveSession,
  SESSION_EXPIRED_EVENT,
  SESSION_REFRESHED_EVENT,
  type AuthState,
  type InviteAccepted,
  type TenantInfo,
  type TotpEnrollResult,
} from "@/lib/auth";
import { clearPwaCaches, sendTenantToSw } from "@/lib/pwa";
import {
  deleteTenant as deleteTenantRequest,
  getMyProfile,
  type MyProfile,
} from "@/lib/sessions";

type AuthStatus = "loading" | "authed" | "guest";

type AuthContextValue = {
  status: AuthStatus;
  tenants: TenantInfo[];
  session: AuthState | null;
  currentTenant: TenantInfo | null;
  /** Display identity from GET /auth/me (redesign-app-panels). */
  profile: MyProfile | null;
  refreshProfile: () => Promise<void>;
  refreshTenants: () => Promise<TenantInfo[]>;
  login: (email: string, password: string, totpCode?: string) => Promise<AuthState>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string, totpCode?: string) => Promise<void>;
  /** Email confirmation (refactor-login-and-signup-flows D2): activating the
   *  account grants the web session, so it lands authenticated. */
  verifyEmail: (token: string) => Promise<AuthState>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  acceptInvite: (token: string) => Promise<InviteAccepted>;
  /** New-account path: creates the account and lands authenticated
   *  (fix-invitations-and-role-ui). */
  acceptInviteNew: (token: string, password: string, name?: string) => Promise<AuthState>;
  createTenant: (name: string) => Promise<AuthState>;
  deleteTenant: (tenantId: string, confirmName: string) => Promise<AuthState>;
  totpEnroll: (password: string) => Promise<TotpEnrollResult>;
  totpVerify: (code: string) => Promise<AuthState>;
  totpDisable: (password: string, code: string) => Promise<AuthState>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** BroadcastChannel name (fix-tenant-switch-isolation): tabs share the
 *  session cookie, so a tenant switch in one tab must re-sync the others
 *  instead of letting a stale tab write to the wrong tenant. */
const TENANT_CHANNEL = "visera:tenant";

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return value;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [session, setSessionState] = useState<AuthState | null>(null);
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const loadedRef = useRef(false);

  const refreshProfile = useCallback(async () => {
    try {
      setProfile(await getMyProfile());
    } catch {
      setProfile(null);
    }
  }, [],);

  const refreshTenants = useCallback(async () => {
    const list = await apiFetch<TenantInfo[]>("/api/auth/tenants");
    setTenants(list);
    return list;
  }, []);

  const refreshSession = useCallback(async () => {
    const fresh = await apiFetch<AuthState>("/api/auth/session");
    saveSession(fresh);
    setSessionState(fresh);
    sendTenantToSw(fresh.tid ?? null);
    return fresh;
  }, []);

  // fix-tenant-switch-isolation: a shared cookie means another tab can switch
  // tenant under us; re-sync from the API so the sidebar/cache follow it.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel(TENANT_CHANNEL);
    ch.onmessage = (e: MessageEvent<{ tid?: string | null }>) => {
      const tid = e.data?.tid ?? null;
      if (!tid) return;
      // sessionStorage is per-tab; only the API knows the cookie's active
      // tenant. Re-sync from it instead of reading stale local state.
      void refreshSession();
    };
    return () => {
      ch.close();
    };
  }, [refreshSession]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void (async () => {
      try {
        // Boot: ask the API which tenant the httpOnly cookie is scoped to.
        // Never guess tenants[0] in a new tab: sessionStorage is per-tab and
        // the sidebar/cache would disagree with the API's actual scope.
        await refreshSession();
        const list = await refreshTenants();
        setTenants(list);
        setStatus("authed");
        await refreshProfile().catch(() => undefined);
      } catch {
        clearSession();
        sendTenantToSw(null);
        setStatus("guest");
      }
    })();
  }, [refreshTenants, refreshProfile, refreshSession]);

  const applySession = useCallback((next: AuthState) => {
    saveSession(next);
    setSessionState(next);
    sendTenantToSw(next.tid ?? null);
  }, []);

  const broadcastTenant = useCallback(async (tid: string | null) => {
    if (typeof BroadcastChannel === "undefined") return;
    try {
      const ch = new BroadcastChannel(TENANT_CHANNEL);
      ch.postMessage({ tid });
      ch.close();
    } catch {
      /* privacy: ignore channel failures */
    }
  }, []);

  const signOutLocal = useCallback(async () => {
    // Pin the SW to "no tenant" before wiping: otherwise an in-flight fetch
    // from the old session could be re-cached after the wipe.
    sendTenantToSw(null);
    clearSession();
    setSessionState(null);
    setTenants([]);
    setProfile(null);
    setStatus("guest");
    await clearPwaCaches();
  }, []);

  // Sliding web session (fix-app-shell-ux): silent refreshes update state;
  // a failed refresh ends the session without a reload.
  useEffect(() => {
    const onRefreshed = (e: Event) => {
      applySession((e as CustomEvent<AuthState>).detail);
    };
    const onExpired = () => {
      void signOutLocal();
    };
    window.addEventListener(SESSION_REFRESHED_EVENT, onRefreshed);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(SESSION_REFRESHED_EVENT, onRefreshed);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, [applySession, signOutLocal]);

  const login = useCallback(
    async (email: string, password: string, totpCode?: string) => {
      const state = await apiFetch<AuthState>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, totp_code: totpCode?.trim() || null }),
      });
      // A shared browser may still hold the previous identity's offline
      // data; never let the new session inherit it.
      await clearPwaCaches();
      applySession(state);
      await refreshTenants().catch(() => undefined);
      await refreshProfile().catch(() => undefined);
      setStatus("authed");
      return state;
    },
    [applySession, refreshTenants, refreshProfile],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch<{ status: string }>("/api/auth/logout", { method: "POST" });
    } finally {
      sendTenantToSw(null);
      clearSession();
      setSessionState(null);
      setTenants([]);
      setProfile(null);
      setStatus("guest");
      // D8: no cached tree/metrics/sessions from another user on disk.
      await clearPwaCaches();
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    // Backend answers symmetric {"status":"ok"} (design D5): the page shows
    // the same "check your inbox" screen regardless of account existence.
    await apiFetch<{ status: string }>("/api/auth/password/forgot", {
      method: "POST",
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
  }, []);

  /** Email confirmation (D2): activating the account grants the web session,
   *  so the page lands authenticated like after login. */
  const verifyEmail = useCallback(
    async (token: string) => {
      const state = await apiFetch<AuthState>("/api/auth/verify-email", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      await clearPwaCaches();
      applySession(state);
      await refreshTenants().catch(() => undefined);
      await refreshProfile().catch(() => undefined);
      setStatus("authed");
      return state;
    },
    [applySession, refreshTenants, refreshProfile],
  );

  const resetPassword = useCallback(
    async (token: string, newPassword: string, totpCode?: string) => {
      await apiFetch<{ status: string }>("/api/auth/password/reset", {
        method: "POST",
        body: JSON.stringify({
          token,
          new_password: newPassword,
          totp_code: totpCode?.trim() || null,
        }),
      });
    },
    [],
  );

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    // Returns a rotated session; the API re-sets the httpOnly cookies so
    // this session stays alive (design D4/D13).
    await apiFetch<{ status: string }>("/api/auth/password/change", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }, []);

  const switchTenant = useCallback(
    async (tenantId: string) => {
      const state = await apiFetch<AuthState>("/api/auth/tenants/switch", {
        method: "POST",
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      // D8: wipe the previous tenant's cached tree/metrics before the new
      // tenant is pinned, so a late fetch cannot re-cache old data.
      await clearPwaCaches();
      applySession(state);
      // Tell every open tab the shared cookie now points at this tenant.
      await broadcastTenant(state.tid ?? null);
    },
    [applySession, broadcastTenant],
  );

  const acceptInvite = useCallback(
    async (token: string) => {
      const accepted = await apiFetch<InviteAccepted>("/api/invitations/accept", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      await refreshTenants();
      if (accepted.tenant_id) {
        await switchTenant(accepted.tenant_id).catch(() => undefined);
      }
      return accepted;
    },
    [refreshTenants, switchTenant],
  );

  const acceptInviteNew = useCallback(
    async (token: string, password: string, name?: string) => {
      const trimmed = name?.trim();
      const state = await apiFetch<AuthState>("/api/auth/accept-invite", {
        method: "POST",
        body: JSON.stringify({
          token,
          password,
          ...(trimmed ? { name: trimmed } : {}),
        }),
      });
      await clearPwaCaches();
      applySession(state);
      await refreshTenants().catch(() => undefined);
      await refreshProfile().catch(() => undefined);
      setStatus("authed");
      return state;
    },
    [applySession, refreshTenants, refreshProfile],
  );

  const deleteTenant = useCallback(
    async (tenantId: string, confirmName: string) => {
      // The API returns a fresh session for the remaining tenant (or for
      // "no tenant"), so the deleted-tid token is never reused.
      const state = await deleteTenantRequest(tenantId, confirmName);
      // The deleted tenant's offline tree/metrics must not survive on disk.
      await clearPwaCaches();
      applySession(state);
      await refreshTenants().catch(() => undefined);
      await refreshProfile().catch(() => undefined);
      return state;
    },
    [applySession, refreshTenants, refreshProfile],
  );

  const createTenant = useCallback(
    async (name: string) => {
      const state = await apiFetch<AuthState>("/api/auth/tenants", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      await clearPwaCaches();
      applySession(state);
      await refreshTenants().catch(() => undefined);
      return state;
    },
    [applySession, refreshTenants],
  );

  const totpEnroll = useCallback(async (password: string) => {
    return apiFetch<TotpEnrollResult>("/api/auth/totp/enroll", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  }, []);

  const totpVerify = useCallback(
    async (code: string) => {
      const state = await apiFetch<AuthState>("/api/auth/totp/verify", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      applySession(state);
      return state;
    },
    [applySession],
  );

  const totpDisable = useCallback(
    async (password: string, code: string) => {
      const state = await apiFetch<AuthState>("/api/auth/totp/disable", {
        method: "POST",
        body: JSON.stringify({ password, code: code.trim() }),
      });
      applySession(state);
      return state;
    },
    [applySession],
  );

  const currentTenant = useMemo(() => {
    if (tenants.length === 0) return null;
    const active = tenants.find((t) => t.tenant_id === session?.tid);
    return active ?? tenants[0] ?? null;
  }, [tenants, session]);

  const value = useMemo(
    () => ({
      status,
      tenants,
      session,
      currentTenant,
      profile,
      refreshProfile,
      refreshTenants,
      login,
      logout,
      forgotPassword,
      resetPassword,
      verifyEmail,
      changePassword,
      switchTenant,
      acceptInvite,
      acceptInviteNew,
      createTenant,
      deleteTenant,
      totpEnroll,
      totpVerify,
      totpDisable,
    }),
    [
      status,
      tenants,
      session,
      currentTenant,
      profile,
      refreshProfile,
      refreshTenants,
      login,
      logout,
      forgotPassword,
      resetPassword,
      verifyEmail,
      changePassword,
      switchTenant,
      acceptInvite,
      acceptInviteNew,
      createTenant,
      deleteTenant,
      totpEnroll,
      totpVerify,
      totpDisable,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}